import React, { useRef, useState } from 'react';
import { useScrollProgress } from '../../hooks/useSceneScrollTimings';
import { MoneyCounter } from '../shared/MoneyCounter';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Scene 3 - Flow
 * 
 * Visualizes savings flowing into 3 jars (Save, Invest, Give).
 * Fill animations tied to scroll progress.
 * 
 * High contrast: White text on navy-teal gradient
 */

const jars = [
  {
    id: "save",
    label: "Save",
    subtitle: "Future stability",
    color: "#11C5D9", // teal
    percentage: 40,
    amount: 20000,
  },
  {
    id: "invest",
    label: "Invest",
    subtitle: "Build wealth",
    color: "#C9A227", // gold
    percentage: 40,
    amount: 20000,
  },
  {
    id: "give",
    label: "Give",
    subtitle: "Create impact",
    color: "#FFB869", // amber
    percentage: 20,
    amount: 10000,
  },
];

export const Scene3Flow = ({ onSceneChange, onProgressUpdate }) => {
  const sectionRef = useRef(null);
  const [fillProgress, setFillProgress] = useState(0);

  // Track scroll progress to drive jar fill animations
  useScrollProgress(sectionRef, (progress) => {
    setFillProgress(progress);
    if (onProgressUpdate) {
      onProgressUpdate(55 + progress * 20); // 55-75% of journey
    }
  });

  // Track scene entry
  React.useEffect(() => {
    if (fillProgress > 0.1 && onSceneChange) {
      onSceneChange(3);
    }
  }, [fillProgress, onSceneChange]);

  const totalSavings = jars.reduce((sum, jar) => sum + jar.amount, 0);

  return (
    <section
      id="flow"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center bg-gradient-flow overflow-hidden pt-20"
      data-testid="flow-section"
    >
      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')] bg-repeat" />

      <div className="max-w-6xl mx-auto w-full px-6 sm:px-8 lg:px-12 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-display font-semibold text-white leading-tight mb-4"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            data-testid="flow-title"
          >
            Once it flows automatically,
            <br />
            freedom compounds.
          </h2>
          <p
            className="text-lg sm:text-xl text-white/80 font-sans max-w-2xl mx-auto"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
            data-testid="flow-line"
          >
            Turn your new savings into lasting growth.
          </p>
        </div>

        {/* Jars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {jars.map((jar) => {
            const fillHeight = fillProgress * jar.percentage;

            return (
              <div
                key={jar.id}
                className="flex flex-col items-center"
                data-testid={`money-jar-${jar.id}`}
              >
                {/* Label */}
                <div className="text-center mb-4">
                  <h3
                    className="text-2xl font-display font-semibold text-white mb-1"
                    style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}
                    data-testid={`jar-label-${jar.id}`}
                  >
                    {jar.label}
                  </h3>
                  <p
                    className="text-sm text-white/70 font-sans"
                    data-testid={`jar-subtitle-${jar.id}`}
                  >
                    {jar.subtitle}
                  </p>
                </div>

                {/* SVG Jar */}
                <div className="relative w-full max-w-[200px] h-[300px]">
                  <svg
                    viewBox="0 0 200 300"
                    className="w-full h-full"
                    data-testid={`jar-svg-${jar.id}`}
                  >
                    {/* Jar outline */}
                    <rect
                      x="30"
                      y="40"
                      width="140"
                      height="220"
                      rx="10"
                      fill="none"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="2"
                    />
                    {/* Jar lid */}
                    <rect
                      x="20"
                      y="20"
                      width="160"
                      height="30"
                      rx="5"
                      fill="rgba(255,255,255,0.1)"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="2"
                    />

                    {/* Fill (animated by scroll) */}
                    <rect
                      x="30"
                      y={260 - (220 * fillHeight) / 100}
                      width="140"
                      height={(220 * fillHeight) / 100}
                      rx="10"
                      fill={jar.color}
                      opacity="0.6"
                      className="transition-all duration-300"
                    />

                    {/* Fill glow effect */}
                    <rect
                      x="30"
                      y={260 - (220 * fillHeight) / 100}
                      width="140"
                      height={(220 * fillHeight) / 100}
                      rx="10"
                      fill={jar.color}
                      opacity="0.2"
                      filter="blur(8px)"
                      className="transition-all duration-300"
                    />
                  </svg>

                  {/* Percentage label */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span
                      className="text-3xl font-mono font-bold text-white"
                      style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
                    >
                      {Math.round(fillHeight)}%
                    </span>
                  </div>
                </div>

                {/* Amount */}
                <p
                  className="text-lg font-mono text-white/80 mt-4"
                  data-testid={`jar-amount-${jar.id}`}
                >
                  ${jar.amount.toLocaleString()}
                </p>
              </div>
            );
          })}
        </div>

        {/* Total Counter */}
        <div className="text-center mb-12">
          <p
            className="text-sm sm:text-base text-white/70 font-sans uppercase tracking-wider mb-4"
            data-testid="flow-counter-label"
          >
            Total Savings Redirected
          </p>
          <MoneyCounter
            end={Math.floor(totalSavings * fillProgress)}
            duration={0.5}
            className="text-4xl sm:text-5xl lg:text-6xl text-white drop-shadow-[0_0_24px_rgba(255,255,255,0.3)]"
            testId="flow-counter"
          />
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => {
              console.log('Monthly Plan CTA clicked');
              // TODO: Open monthly plan builder or navigate to /plan
              window.alert('📅 Creating your monthly automation plan...\n\nWe\'ll show you how to automate your savings.');
            }}
            className="h-14 px-8 text-base font-semibold bg-teal-400 hover:bg-teal-500 text-slate-900 shadow-xl hover:shadow-glow-teal transition-all duration-300"
            data-testid="monthly-plan-cta-button"
          >
            Show My Monthly Plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Subtext */}
        <p
          className="text-center text-sm sm:text-base text-white/60 font-sans mt-12"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
          data-testid="flow-subtext"
        >
          Automation turns effort into ease.
        </p>
      </div>
    </section>
  );
};